import { Inertia, Page } from '@inertiajs/inertia'
import { Build, Component, h, Listen, Prop } from '@stencil/core'
import Axios from 'axios'

if (Build.isBrowser) {
  import('intersection-observer')
}

import { Fragment, Method } from '@stencil/core/internal'
import * as fastdom from 'fastdom'
import Pusher from 'pusher-js'
import { logWithBadge } from 'src/utils/logging'
import { configure } from 'src/zoom-app/utils'

import { AppSharedPropsStore, ILayoutProps, SharedProps } from '../../app'
import { GlobalNavStore } from '../../app/nav'
import { sendLogs } from '../../utils/new_relic'
import { loadSegment, SegmentStore, shouldTrackSession } from './segment'

let mustInitializePusher = true
let pusher: Pusher

@Component({
  tag: 'inertia-app',
})
export class App {
  @Prop({ mutable: true }) page: {
    component?: string
    props?: Partial<SharedProps>
  }

  innerHeight: number = window.innerHeight
  el: HTMLElement
  layoutEl: ILayoutProps & HTMLElement
  pageEl: HTMLElement
  pageChanged = true
  rendered = false

  resetSubnavTimeout: number

  /**
   * Prevent users from pasting rich text/markup into contenteditable elements
   */
  @Listen('paste', { target: 'body' })
  pasteTextOnly(e: ClipboardEvent) {
    const target = e.target as HTMLElement
    if (target.isContentEditable) {
      const text = e.clipboardData.getData('text/plain')
      const selection = window.getSelection()
      if (!selection.rangeCount) return false
      selection.deleteFromDocument()
      selection.getRangeAt(0).insertNode(document.createTextNode(text))
      e.preventDefault()
    }
  }

  @Listen('resize', { target: 'window', passive: true, capture: true })
  onResize() {
    fastdom.measure(() => {
      this.innerHeight = window.innerHeight
    })
    fastdom.mutate(() => {
      if (this.layoutEl) {
        this.layoutEl.style.height = this.innerHeight + 'px'
      } else if (this.pageEl) {
        this.pageEl.style.height = this.innerHeight + 'px'
      }
    })
  }

  @Method()
  async waitFor(promise: Promise<unknown>) {
    await promise
    this.hideLoader()
  }

  @Method()
  async hideLoader() {
    if (this.layoutEl) {
      this.layoutEl.showLoader = false
    }
  }

  async updatePage(component, props: SharedProps) {
    this.pageChanged = !this.page || this.page.component !== component

    // If the new page doesn't define a subnav, make sure to hide any existing subnav
    this.resetSubnavTimeout = window.setTimeout(() => {
      GlobalNavStore.state.subnav = null
    })

    if (props) {
      AppSharedPropsStore.reset()
      for (const propName of Object.keys(props)) {
        AppSharedPropsStore.state[propName] = props[propName]
      }
    }

    if (props.head?.title) {
      document.title = props.head.title
    }

    if (props.env.RAILS_ENV === 'production') {
      // Mux Production Environment Key
      AppSharedPropsStore.state.muxEnvKey = 'bscpg7itsku03ntj128u4t606'
    } else {
      // Mux Development Environment Key
      AppSharedPropsStore.state.muxEnvKey = '170g3u4tt5u8dsovb5cc7dldg'
    }

    if (!SegmentStore.state.analytics?.initialized && shouldTrackSession(props)) {
      logWithBadge('Segment', 'loading')
      // We want this initialized before Inertia calls updatePage
      if (props.env.RAILS_ENV === 'production') {
        await loadSegment('SsnR2mwr8AyPMAMsb33uv1L5PRZWRGgf')
      } else if (props.env.RAILS_ENV === 'development') {
        await loadSegment('YlPoJrH7BczAdsOKuLX2OGv194LGefGI')
      }
    }

    if (SegmentStore.state.analytics?.initialized) {
      if (!shouldTrackSession(props)) {
        logWithBadge('Segment', 'unloading')
        SegmentStore.reset()
      } else {
        try {
          if (this.pageChanged) {
            SegmentStore.state.analytics.page({
              environment: props.env.RAILS_ENV,
              appVersion: props.env.APP_VERSION,
              title: '',
            })
          }

          if (props.currentUser && !props.adminConfig?.isImpersonating) {
            const userId = props.currentUser.id.toString()
            const segmentUserId = SegmentStore.state.analytics.user().id()
            if (segmentUserId !== userId) {
              SegmentStore.state.analytics.identify(userId)
            }
          }
        } catch (error) {
          if (error instanceof Error) {
            window.Sentry?.captureException(error)
          }
        }
      }
    }

    const usePusherViaPage = !!props.pusherConfig && (!props.usePusherViaElectron || !window.electronAPI?.initPusher)
    if (usePusherViaPage) {
      logWithBadge('Pusher', 'Use via page')
    } else if (!!props.pusherConfig && props.usePusherViaElectron && !!window.electronAPI?.initPusher) {
      logWithBadge('Pusher', 'Use via electron')
    } else if (props.pusherConfig) {
      logWithBadge('Pusher', 'Found config but cannot use via page or electron')
    } else {
      logWithBadge('Pusher', 'No config')
    }
    // Only configure pusher for pages that have a config
    if (usePusherViaPage && mustInitializePusher) {
      logWithBadge('Pusher', 'Initializing client')
      mustInitializePusher = false
      // disable sockjs because it needs wildcard domains for csp
      // https://support.pusher.com/hc/en-us/articles/360021061813-What-Content-Security-Policy-CSP-do-I-need-for-Channels-
      pusher = new Pusher(props.env.PUSHER_KEY, {
        cluster: props.env.PUSHER_CLUSTER,
        disabledTransports: ['sockjs'],
        disableStats: true,
        channelAuthorization: {
          endpoint: props.env.PUSHER_AUTH_URL,
          transport: 'ajax',
          customHandler: (params, callback) => {
            Axios.post(props.env.PUSHER_AUTH_URL, { cluster: props.env.PUSHER_CLUSTER, ...params })
              .then((response) => callback(null, response.data))
              .catch((reason) => callback(reason, null))
          },
        },
      })
      pusher.connection.bind('state_change', (states) => {
        sendLogs(props.newRelicLogsUrl, {
          pusher: {
            action: 'state_change',
            previous_state: states.previous,
            current_state: states.current,
          },
          frontend: {
            version: props.env.APP_VERSION,
            component: component,
          },
        })
      })
      pusher.connection.bind('error', (error) => {
        sendLogs(props.newRelicLogsUrl, {
          pusher: {
            action: 'connection_error',
            debug: error,
          },
          frontend: {
            version: props.env.APP_VERSION,
            component: component,
          },
        })
      })
    }
    if (this.pageChanged && pusher) {
      const channels = pusher.allChannels() || []
      channels.map((c) => pusher.unsubscribe(c.name))
    }
    this.page = { component, props }

    return Promise.resolve()
  }

  async componentWillLoad() {
    const appEl = document.getElementById('app')
    if (appEl) {
      const initialPage: Page<SharedProps> = JSON.parse(appEl.dataset.page)

      Inertia.init({
        initialPage,
        resolveComponent: (name: string) => name,
        swapComponent: ({ component, page: { props } }) => {
          return this.updatePage(component, props as any)
        },
      })

      Inertia.on('invalid', (event) => {
        event.preventDefault()
        return true
      })

      if (initialPage.props.inZapp) {
        await configure()
        document.addEventListener('inertia:navigate', async () => {
          await configure()
        })
      }
    }
  }

  componentDidLoad() {
    this.rendered = true
    // When/if a subnav is defined during page transition make sure we don't hide
    // the subnav prematurely
    GlobalNavStore.onChange('subnav', () => window.clearTimeout(this.resetSubnavTimeout))
  }

  render() {
    if (!this.page) {
      // First run, wait for Inertia
      return
    }
    const { props, component: Page } = this.page

    if (props.layout) {
      const {
        layout: { component: Layout, props: layoutProps },
      } = props
      return (
        <Fragment>
          <Layout
            ref={(el) => (this.layoutEl = el)}
            class='w-screen h-screen'
            style={{ height: this.innerHeight + 'px' }}
            pusherClient={pusher}
            {...layoutProps}
            {...props}
          >
            <Page pusherClient={pusher} {...props} />
          </Layout>
        </Fragment>
      )
    } else {
      return (
        <Page
          ref={(el) => (this.pageEl = el)}
          class='w-screen h-screen'
          style={{ height: this.innerHeight + 'px', background: '#1A1A1A' }}
          {...props}
        />
      )
    }
  }
}
